import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

export function useMarkets() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.markets().then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);
  return { data, loading };
}

export function useQuote(ticker, market) {
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetch_ = useCallback(async () => {
    if (!ticker) return;
    setLoading(true); setError(null);
    try { setData(await api.quote(ticker, market)); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [ticker, market]);

  useEffect(() => { fetch_(); }, [fetch_]);
  return { data, loading, error, refetch: fetch_ };
}

export function useHistory(ticker, period = '3mo', market) {
  const [data, setData]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ticker) return;
    setLoading(true);
    api.history(ticker, period, market)
      .then(setData).catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [ticker, period, market]);

  return { data, loading, error };
}

export function useTechnical(ticker, market, timeframe = 'daily') {
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ticker) return;
    setLoading(true);
    api.technical(ticker, market, timeframe)
      .then(setData).catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [ticker, market, timeframe]);

  return { data, loading, error };
}

export function useInsights(ticker, market) {
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ticker) return;
    setLoading(true);
    api.insights(ticker, market)
      .then(setData).catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [ticker, market]);

  return { data, loading, error };
}

export function useFinancials(ticker, market) {
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ticker) return;
    setLoading(true);
    api.financials(ticker, market)
      .then(setData).catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [ticker, market]);

  return { data, loading, error };
}

export function useNews(ticker, name, market) {
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ticker) return;
    setLoading(true);
    api.news(ticker, name, market)
      .then(setData).catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [ticker, name, market]);

  return { data, loading, error };
}

export function useWatchlist() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.watchlist.get();
      setItems(r.items || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const isWatched = useCallback(
    (ticker, market) => items.some(
      i => i.ticker?.toUpperCase() === ticker?.toUpperCase() &&
           i.market?.toUpperCase() === market?.toUpperCase()),
    [items]);

  const add = useCallback(async (item) => {
    const r = await api.watchlist.add(item);
    setItems(r.items || []);
  }, []);

  const remove = useCallback(async (market, ticker) => {
    const r = await api.watchlist.remove(market, ticker);
    setItems(r.items || []);
  }, []);

  const toggle = useCallback(async (item) => {
    if (items.some(i => i.ticker?.toUpperCase() === item.ticker?.toUpperCase() &&
                        i.market?.toUpperCase() === item.market?.toUpperCase())) {
      await remove(item.market, item.ticker);
    } else {
      await add(item);
    }
  }, [items, add, remove]);

  const importItems = useCallback(async (arr) => {
    const r = await api.watchlist.import(arr);
    setItems(r.items || []);
  }, []);

  return { items, loading, refresh, isWatched, add, remove, toggle, importItems };
}
